import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Plus, DollarSign, Fuel, Wrench, CircleDot, Users, MoreHorizontal, Trash2 } from 'lucide-react';
import { format, subMonths } from 'date-fns';
import { ExportButton } from '../utils/exportUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COST_CATEGORIES = [
  { key: 'diesel_cost', label: 'Diesel', icon: Fuel, color: 'text-amber-600' },
  { key: 'maintenance_cost', label: 'Maintenance', icon: Wrench, color: 'text-blue-600' },
  { key: 'tyre_cost', label: 'Tyres', icon: CircleDot, color: 'text-slate-600' },
  { key: 'wages_cost', label: 'Wages', icon: Users, color: 'text-green-600' },
  { key: 'other_cost', label: 'Others', icon: MoreHorizontal, color: 'text-purple-600' },
];

const OwningCostTab = ({ user }) => {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState([]);
  const [assets, setAssets] = useState([]);
  const [autoDiesel, setAutoDiesel] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'yyyy-MM'));
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [viewMode, setViewMode] = useState('monthly'); // monthly or summary
  const [dieselPrice, setDieselPrice] = useState(95);
  
  const [form, setForm] = useState({
    door_no: '',
    month: format(new Date(), 'yyyy-MM'),
    diesel_cost: '',
    maintenance_cost: '',
    tyre_cost: '',
    wages_cost: '',
    other_cost: '',
    notes: ''
  });

  // Generate last 12 months for selection
  const monthOptions = Array.from({ length: 12 }, (_, i) => {
    const date = subMonths(new Date(), i);
    return {
      value: format(date, 'yyyy-MM'),
      label: format(date, 'MMMM yyyy')
    };
  });

  useEffect(() => {
    fetchData();
  }, [selectedMonth, dieselPrice]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [entriesRes, summaryRes, assetsRes, autoDieselRes] = await Promise.all([
        axios.get(`${API}/owning-cost?month=${selectedMonth}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/owning-cost/summary?month=${selectedMonth}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/assets`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/owning-cost/auto-diesel?month=${selectedMonth}&diesel_price_per_liter=${dieselPrice}`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setEntries(entriesRes.data);
      setSummary(summaryRes.data);
      setAssets(assetsRes.data);
      
      // Create a map of door_no -> diesel cost from auto-diesel data
      const dieselMap = {};
      if (autoDieselRes.data.vehicles) {
        autoDieselRes.data.vehicles.forEach(v => {
          dieselMap[v.door_no] = { cost: v.diesel_cost, liters: v.total_liters };
        });
      }
      setAutoDiesel(dieselMap);
    } catch (error) {
      toast.error('Failed to fetch owning cost data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/owning-cost`, {
        ...form,
        diesel_cost: parseFloat(form.diesel_cost) || 0,
        maintenance_cost: parseFloat(form.maintenance_cost) || 0,
        tyre_cost: parseFloat(form.tyre_cost) || 0,
        wages_cost: parseFloat(form.wages_cost) || 0,
        other_cost: parseFloat(form.other_cost) || 0,
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Cost entry saved!');
      setIsAddOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save entry');
    }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Delete this cost entry?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/owning-cost/${entryId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Entry deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const resetForm = () => {
    setForm({
      door_no: '',
      month: selectedMonth,
      diesel_cost: '',
      maintenance_cost: '',
      tyre_cost: '',
      wages_cost: '',
      other_cost: '',
      notes: ''
    });
  };

  // Auto-populate diesel cost when vehicle is selected
  const handleVehicleSelect = (doorNo) => {
    const dieselData = autoDiesel[doorNo];
    setForm(prev => ({
      ...prev,
      door_no: doorNo,
      diesel_cost: dieselData ? dieselData.cost.toString() : ''
    }));
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  // Calculate totals for the selected month
  const monthlyTotals = entries.reduce((acc, entry) => {
    acc.diesel += entry.diesel_cost || 0;
    acc.maintenance += entry.maintenance_cost || 0;
    acc.tyres += entry.tyre_cost || 0;
    acc.wages += entry.wages_cost || 0;
    acc.other += entry.other_cost || 0;
    acc.total += entry.total_cost || 0;
    return acc;
  }, { diesel: 0, maintenance: 0, tyres: 0, wages: 0, other: 0, total: 0 });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Owning Cost Report</h2>
          <p className="text-slate-600 text-sm mt-1">Track vehicle-wise monthly costs • Auto-populated from diesel logs</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 bg-amber-50 px-3 py-1.5 rounded-lg border border-amber-200">
            <Fuel className="h-4 w-4 text-amber-600" />
            <span className="text-sm text-amber-700">₹</span>
            <Input
              type="number"
              value={dieselPrice}
              onChange={(e) => setDieselPrice(parseFloat(e.target.value) || 95)}
              className="w-16 h-7 text-sm border-amber-300"
              title="Diesel price per liter"
            />
            <span className="text-xs text-amber-600">/L</span>
          </div>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select month" />
            </SelectTrigger>
            <SelectContent>
              {monthOptions.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => { resetForm(); setIsAddOpen(true); }} className="bg-slate-900 hover:bg-slate-800" data-testid="add-cost-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Cost Entry
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
        {COST_CATEGORIES.map(cat => {
          const Icon = cat.icon;
          const value = monthlyTotals[cat.key.replace('_cost', '')] || 0;
          return (
            <Card key={cat.key}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`h-4 w-4 ${cat.color}`} />
                  <span className="text-xs text-slate-500 font-medium">{cat.label}</span>
                </div>
                <p className="text-lg font-bold">{formatCurrency(value)}</p>
              </CardContent>
            </Card>
          );
        })}
        <Card className="bg-slate-900 text-white">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="h-4 w-4" />
              <span className="text-xs text-slate-300 font-medium">Total</span>
            </div>
            <p className="text-lg font-bold">{formatCurrency(monthlyTotals.total)}</p>
          </CardContent>
        </Card>
      </div>

      {/* View Toggle */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button 
            variant={viewMode === 'monthly' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setViewMode('monthly')}
          >
            Monthly Details
          </Button>
          <Button 
            variant={viewMode === 'summary' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setViewMode('summary')}
          >
            Vehicle Summary
          </Button>
        </div>
        <ExportButton
          rows={viewMode === 'monthly' ? entries : summary}
          columns={viewMode === 'monthly' ? [
            { header: 'Month', key: 'month' },
            { header: 'Asset', key: 'asset_door_no' },
            { header: 'Diesel (₹)', key: 'diesel_cost' },
            { header: 'Maintenance (₹)', key: 'maintenance_cost' },
            { header: 'Tyres (₹)', key: 'tyre_cost' },
            { header: 'Wages (₹)', key: 'wages_cost' },
            { header: 'Other (₹)', key: 'other_cost' },
            { header: 'Total (₹)', key: r => ((r.diesel_cost||0)+(r.maintenance_cost||0)+(r.tyre_cost||0)+(r.wages_cost||0)+(r.other_cost||0)).toFixed(2) },
          ] : [
            { header: 'Asset', key: 'asset_door_no' },
            { header: 'Total Diesel (₹)', key: 'total_diesel' },
            { header: 'Total Maintenance (₹)', key: 'total_maintenance' },
            { header: 'Total Tyres (₹)', key: 'total_tyres' },
            { header: 'Total Wages (₹)', key: 'total_wages' },
            { header: 'Total Other (₹)', key: 'total_other' },
            { header: 'Grand Total (₹)', key: 'grand_total' },
          ]}
          filename={`owning_cost_${viewMode}_${selectedMonth}`}
          title="Owning Cost Report"
          subtitle={`Month: ${selectedMonth} | Exported on ${new Date().toLocaleDateString('en-IN')}`}
        />
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg border shadow-sm">
        {viewMode === 'monthly' ? (
          <>
            <div className="p-4 border-b">
              <h3 className="font-semibold">Cost Entries for {monthOptions.find(m => m.value === selectedMonth)?.label}</h3>
            </div>
            {entries.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No cost entries for this month. Click "Add Cost Entry" to start tracking.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle</TableHead>
                    <TableHead className="text-right">Diesel</TableHead>
                    <TableHead className="text-right">Maintenance</TableHead>
                    <TableHead className="text-right">Tyres</TableHead>
                    <TableHead className="text-right">Wages</TableHead>
                    <TableHead className="text-right">Others</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="w-16">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {entries.map((entry) => {
                    const asset = assets.find(a => a.door_no === entry.door_no);
                    return (
                      <TableRow key={entry.id} data-testid={`cost-row-${entry.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{entry.door_no}</p>
                            {asset && (
                              <p className="text-xs text-slate-500">{asset.make} {asset.model}</p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.diesel_cost)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.maintenance_cost)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.tyre_cost)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.wages_cost)}</TableCell>
                        <TableCell className="text-right">{formatCurrency(entry.other_cost)}</TableCell>
                        <TableCell className="text-right font-semibold">{formatCurrency(entry.total_cost)}</TableCell>
                        <TableCell className="text-sm text-slate-500 max-w-[150px] truncate">{entry.notes || '-'}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleDelete(entry.id)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </>
        ) : (
          <>
            <div className="p-4 border-b">
              <h3 className="font-semibold">Vehicle-wise Cost Summary</h3>
            </div>
            {summary.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                No data available for summary.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead className="text-right">Diesel</TableHead>
                    <TableHead className="text-right">Maintenance</TableHead>
                    <TableHead className="text-right">Tyres</TableHead>
                    <TableHead className="text-right">Wages</TableHead>
                    <TableHead className="text-right">Others</TableHead>
                    <TableHead className="text-right">Grand Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {summary.map((item) => (
                    <TableRow key={item.door_no}>
                      <TableCell>
                        <p className="font-medium">{item.door_no}</p>
                        <p className="text-xs text-slate-500">{item.make} {item.model}</p>
                      </TableCell>
                      <TableCell>{item.asset_type}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.total_diesel)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.total_maintenance)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.total_tyres)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.total_wages)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(item.total_other)}</TableCell>
                      <TableCell className="text-right font-bold">{formatCurrency(item.grand_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Cost Entry</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Vehicle *</Label>
                <Select value={form.door_no} onValueChange={handleVehicleSelect}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select vehicle" />
                  </SelectTrigger>
                  <SelectContent>
                    {assets.map(a => (
                      <SelectItem key={a.id} value={a.door_no}>
                        {a.door_no} - {a.make} {a.model}
                        {autoDiesel[a.door_no] && (
                          <span className="ml-2 text-xs text-green-600">
                            ({autoDiesel[a.door_no].liters.toFixed(0)}L)
                          </span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Month *</Label>
                <Select value={form.month} onValueChange={(val) => setForm({ ...form, month: val })}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder="Select month" />
                  </SelectTrigger>
                  <SelectContent>
                    {monthOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="flex items-center gap-2">
                  Diesel Cost (₹)
                  {form.door_no && autoDiesel[form.door_no] && (
                    <span className="text-xs font-normal text-green-600 bg-green-50 px-2 py-0.5 rounded">
                      Auto: {autoDiesel[form.door_no].liters.toFixed(1)}L × ₹{dieselPrice}
                    </span>
                  )}
                </Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.diesel_cost}
                  onChange={(e) => setForm({ ...form, diesel_cost: e.target.value })}
                  placeholder="0"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Maintenance Cost (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.maintenance_cost}
                  onChange={(e) => setForm({ ...form, maintenance_cost: e.target.value })}
                  placeholder="0"
                  className="mt-1.5"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Tyre Cost (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.tyre_cost}
                  onChange={(e) => setForm({ ...form, tyre_cost: e.target.value })}
                  placeholder="0"
                  className="mt-1.5"
                />
              </div>
              <div>
                <Label>Wages Cost (₹)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={form.wages_cost}
                  onChange={(e) => setForm({ ...form, wages_cost: e.target.value })}
                  placeholder="0"
                  className="mt-1.5"
                />
              </div>
            </div>
            
            <div>
              <Label>Other Costs (₹)</Label>
              <Input
                type="number"
                step="0.01"
                value={form.other_cost}
                onChange={(e) => setForm({ ...form, other_cost: e.target.value })}
                placeholder="0"
                className="mt-1.5"
              />
            </div>
            
            <div>
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Optional notes..."
                className="mt-1.5"
              />
            </div>
            
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsAddOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="save-cost-btn">
                Save Entry
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OwningCostTab;
